// src/components/groups/GroupDetail.jsx
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetGroupByIdQuery,
  useGetGroupStatisticsQuery,
  useRemoveDevicesFromGroupMutation,
  useRemoveManagerFromGroupMutation,
  useRemoveViewerFromGroupMutation,
} from "../../redux/admin/groupApi";
import { toast } from "react-hot-toast";

import AddViewerModal from "./AddViewerModal";
import AddDeviceToGroupModal from "./AddDeviceToGroupModal";
import AddManagerModal from "./AddManagerModal";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// ✅ MUI DataGrid
import { DataGrid } from "@mui/x-data-grid";

import {
  Users,
  Smartphone,
  Plus,
  ArrowLeft,
  Trash2,
  BarChart3,
  ShieldCheck,
  Eye,
  Edit2,
  RefreshCw,
  MapPin,
} from "lucide-react";

const StatCard = ({ label, value, subValue, icon, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={900}>
          {value}
        </Typography>
        {subValue ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {subValue}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          background: alpha(color || theme.palette.primary.main, 0.12),
          color: color || theme.palette.primary.main,
        }}
      >
        {icon}
      </Box>
    </Paper>
  );
};

const ConfirmDialog = ({ open, title, desc, loading, onClose, onConfirm }) => (
  <Dialog open={open} onClose={loading ? () => {} : onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>
    <DialogContent>
      <Typography sx={{ color: "text.secondary" }}>{desc}</Typography>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} disabled={loading} sx={{ borderRadius: 2, fontWeight: 800 }}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={loading}
        variant="contained"
        color="error"
        startIcon={loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <Trash2 size={16} />}
        sx={{ borderRadius: 2, fontWeight: 900 }}
      >
        Remove
      </Button>
    </DialogActions>
  </Dialog>
);

export default function GroupDetail() {
  const { id: groupId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));

  // modals
  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);

  // confirm state
  const [confirm, setConfirm] = useState({ open: false, type: null, payload: null });

  // queries
  const { data: groupData, isLoading, error, refetch } = useGetGroupByIdQuery(groupId);
  const { data: statsData, refetch: refetchStats } = useGetGroupStatisticsQuery(groupId);

  // mutations
  const [removeDevice, { isLoading: isRemovingDevice }] = useRemoveDevicesFromGroupMutation();
  const [removeManager, { isLoading: isRemovingManager }] = useRemoveManagerFromGroupMutation();
  const [removeViewer, { isLoading: isRemovingViewer }] = useRemoveViewerFromGroupMutation();

  const group = groupData?.data;
  const stats = statsData?.data;

  const headerColor = group?.color || "#3b82f6";

  const handleRefresh = () => {
    refetch();
    refetchStats();
    toast.success("Data refreshed");
  };

  const openRemove = (type, payload) => setConfirm({ open: true, type, payload });
  const closeRemove = () => setConfirm({ open: false, type: null, payload: null });

  const confirmLoading =
    (confirm.type === "device" && isRemovingDevice) ||
    (confirm.type === "manager" && isRemovingManager) ||
    (confirm.type === "viewer" && isRemovingViewer);

  const handleConfirmRemove = async () => {
    try {
      if (confirm.type === "device") {
        const device = confirm.payload;
        await removeDevice({ groupId, deviceIds: [device._id] }).unwrap();
        toast.success("Device removed successfully");
        closeRemove();
        refetch();
        refetchStats();
      }
      if (confirm.type === "manager") {
        const userId = confirm.payload;
        await removeManager({ groupId, userId }).unwrap();
        toast.success("Manager removed successfully");
        closeRemove();
        refetch();
      }
      if (confirm.type === "viewer") {
        const userId = confirm.payload;
        await removeViewer({ groupId, userId }).unwrap();
        toast.success("Viewer removed successfully");
        closeRemove();
        refetch();
      }
    } catch (err) {
      toast.error(err?.data?.msg || "Remove failed");
    }
  };

  const deviceRows = useMemo(() => {
    const list = group?.devices || [];
    return list.map((d) => ({
      id: d._id,
      name: d.name || "Unnamed",
      imei: d.imei || "—",
      status: d.status || "offline",
      speed: d.liveData?.speed ?? 0,
      model: d.model || "",
      raw: d,
    }));
  }, [group]);

  const deviceCols = useMemo(
    () => [
      {
        field: "name",
        headerName: "Device",
        flex: 1,
        minWidth: 220,
        renderCell: (p) => {
          const online = p.row.status === "online";
          return (
            <Stack
              onClick={() => navigate(`/devices/${p.row.id}`)}
              sx={{ cursor: "pointer" }}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 99,
                  background: online ? theme.palette.success.main : theme.palette.text.disabled,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={900} noWrap>
                  {p.row.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                  {p.row.model || "—"}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      { field: "imei", headerName: "IMEI", flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.value === "online" ? "Online" : "Offline"}
            color={p.value === "online" ? "success" : "default"}
            variant={p.value === "online" ? "filled" : "outlined"}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          />
        ),
      },
      {
        field: "speed",
        headerName: "Speed",
        minWidth: 130,
        renderCell: (p) =>
          p.value > 0 ? (
            <Stack direction="row" spacing={0.6} alignItems="center">
              <MapPin size={14} />
              <Typography fontWeight={800}>{p.value} km/h</Typography>
            </Stack>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>—</Typography>
          ),
      },
      {
        field: "actions",
        headerName: "Action",
        sortable: false,
        filterable: false,
        width: 110,
        renderCell: (p) => (
          <Tooltip title="Remove from group">
            <span>
              <IconButton
                onClick={() => openRemove("device", p.row.raw)}
                disabled={isRemovingDevice}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  background: alpha(theme.palette.error.main, 0.08),
                }}
              >
                <Trash2 size={18} />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [navigate, theme, isRemovingDevice]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}
        >
          <Stack spacing={2}>
            <Skeleton width="55%" height={34} />
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={420} />
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}
        >
          <Alert
            severity="error"
            action={
              <Button onClick={() => refetch()} size="small">
                Retry
              </Button>
            }
          >
            {error?.data?.msg || "Error loading group details"}
          </Alert>
        </Paper>
      </Box>
    );
  }

  const managers = group.managers?.filter((m) => m.role !== "owner") || [];
  const viewers = group.viewers || [];
  const childGroups = group.childGroups || [];

  return (
    <Box sx={{ minHeight: "100%", pb: 3 }}>
      {/* ✅ Gradient Header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 2.2, md: 3 },
          background: `linear-gradient(135deg, ${headerColor} 0%, ${headerColor}cc 100%)`,
        }}
      >
        <Stack spacing={1.6} sx={{ maxWidth: 1200, mx: "auto" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              onClick={() => navigate("/groups")}
              startIcon={<ArrowLeft size={18} />}
              sx={{
                borderRadius: 2,
                color: "white",
                borderColor: alpha("#fff", 0.6),
                "&:hover": { borderColor: "#fff", background: alpha("#fff", 0.12) },
              }}
            >
              Back to Groups
            </Button>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  sx={{
                    borderRadius: 2,
                    background: alpha("#fff", 0.14),
                    color: "white",
                    "&:hover": { background: alpha("#fff", 0.2) },
                  }}
                >
                  <RefreshCw size={18} />
                </IconButton>
              </Tooltip>

              <Button
                onClick={() => navigate(`/groups/${groupId}/edit`)}
                variant="contained"
                startIcon={<Edit2 size={18} />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 900,
                  background: alpha("#000", 0.18),
                  boxShadow: "none",
                  "&:hover": { background: alpha("#000", 0.25), boxShadow: "none" },
                }}
              >
                Edit
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.4} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: alpha("#fff", 0.16),
                  color: "white",
                  flexShrink: 0,
                }}
              >
                <Users size={28} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" fontWeight={1000} sx={{ color: "white" }} noWrap>
                  {group.name}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    size="small"
                    label={group.groupCode || "—"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      background: alpha("#fff", 0.16),
                      color: "white",
                    }}
                  />
                  <Chip
                    size="small"
                    label={(group.groupType || "custom").replaceAll("_", " ")}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      background: alpha("#fff", 0.16),
                      color: "white",
                      textTransform: "capitalize",
                    }}
                  />
                </Stack>

                {group.description ? (
                  <Typography sx={{ color: alpha("#fff", 0.9), mt: 0.6 }} variant="body2">
                    {group.description}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
              <Button
                onClick={() => setIsDeviceModalOpen(true)}
                variant="contained"
                startIcon={<Smartphone size={18} />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 900,
                  background: alpha("#fff", 0.16),
                  boxShadow: "none",
                  color: "white",
                  "&:hover": { background: alpha("#fff", 0.24), boxShadow: "none" },
                }}
                fullWidth={isDownMd}
              >
                Add Device
              </Button>

              <Button
                onClick={() => setIsViewerModalOpen(true)}
                variant="contained"
                startIcon={<Eye size={18} />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 900,
                  background: alpha("#fff", 0.16),
                  boxShadow: "none",
                  color: "white",
                  "&:hover": { background: alpha("#fff", 0.24), boxShadow: "none" },
                }}
                fullWidth={isDownMd}
              >
                Add Viewer
              </Button>

              <Button
                onClick={() => setIsManagerModalOpen(true)}
                variant="contained"
                startIcon={<ShieldCheck size={18} />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 900,
                  background: alpha("#fff", 0.16),
                  boxShadow: "none",
                  color: "white",
                  "&:hover": { background: alpha("#fff", 0.24), boxShadow: "none" },
                }}
                fullWidth={isDownMd}
              >
                Add Manager
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* ✅ Body */}
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, mt: 2.2 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Stats */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total Devices"
                value={stats?.devices?.total || group.devices?.length || 0}
                subValue={`${stats?.devices?.online || 0} online`}
                icon={<Smartphone size={20} />}
                color={theme.palette.info.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Managers"
                value={group.managers?.length || 0}
                icon={<ShieldCheck size={20} />}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Viewers"
                value={group.viewers?.length || 0}
                icon={<Eye size={20} />}
                color={theme.palette.warning.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Moving Now"
                value={stats?.devices?.moving || 0}
                subValue={`${stats?.devices?.idle || 0} idle`}
                icon={<BarChart3 size={20} />}
                color={theme.palette.success.main}
              />
            </Grid>
          </Grid>

          {/* Main layout */}
          <Grid container spacing={2}>
            {/* Left: Devices */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.3,
                        display: "grid",
                        placeItems: "center",
                        background: alpha(theme.palette.info.main, 0.12),
                        color: theme.palette.info.main,
                      }}
                    >
                      <Smartphone size={18} />
                    </Box>
                    <Box>
                      <Typography fontWeight={900}>Devices</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {deviceRows.length} total
                      </Typography>
                    </Box>
                  </Stack>

                  <Button
                    variant="contained"
                    onClick={() => setIsDeviceModalOpen(true)}
                    startIcon={<Plus size={16} />}
                    sx={{ borderRadius: 2, fontWeight: 900, boxShadow: "none" }}
                  >
                    Add Device
                  </Button>
                </Stack>

                <Divider />

                <Box sx={{ height: 520, width: "100%" }}>
                  <DataGrid
                    rows={deviceRows}
                    columns={deviceCols}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    }}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-columnHeaders": {
                        background: alpha(theme.palette.primary.main, 0.06),
                        borderRadius: 2,
                      },
                      "& .MuiDataGrid-cell": { outline: "none" },
                    }}
                  />
                </Box>

                {deviceRows.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No devices in this group. Click “Add Device” to assign.
                  </Alert>
                ) : null}
              </Paper>
            </Grid>

            {/* Right: Owner + Managers + Viewers + SubGroups */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                {/* Owner */}
                {group.owner ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2.3,
                          display: "grid",
                          placeItems: "center",
                          background: alpha(theme.palette.success.main, 0.12),
                          color: theme.palette.success.main,
                        }}
                      >
                        👑
                      </Box>
                      <Typography fontWeight={900}>Group Owner</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar sx={{ bgcolor: alpha(headerColor, 0.9), fontWeight: 900 }}>
                        {group.owner.username?.charAt(0)?.toUpperCase() || "O"}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} noWrap>
                          {group.owner.username || "Unknown"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                          {group.owner.email || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ) : null}

                {/* Managers */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.2,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2.3,
                          display: "grid",
                          placeItems: "center",
                          background: alpha(theme.palette.secondary.main, 0.12),
                          color: theme.palette.secondary.main,
                        }}
                      >
                        <ShieldCheck size={18} />
                      </Box>
                      <Box>
                        <Typography fontWeight={900}>Managers</Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {managers.length} assigned
                        </Typography>
                      </Box>
                    </Stack>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setIsManagerModalOpen(true)}
                      sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                      Add
                    </Button>
                  </Stack>

                  <Divider />

                  {managers.length ? (
                    <Stack spacing={1}>
                      {managers.map((m) => {
                        const user = m.user || m;
                        return (
                          <Paper
                            key={user._id}
                            elevation={0}
                            sx={{
                              p: 1.2,
                              borderRadius: 2.5,
                              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.8) }}>
                                {user.username?.charAt(0)?.toUpperCase() || "M"}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={900} noWrap>
                                  {user.username || "Unknown"}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                                  {user.email || "—"}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={m.role || "manager"}
                                  variant="outlined"
                                  sx={{ mt: 0.5, borderRadius: 2, fontWeight: 800 }}
                                />
                              </Box>
                            </Stack>

                            <Tooltip title="Remove">
                              <span>
                                <IconButton
                                  onClick={() => openRemove("manager", user._id)}
                                  disabled={isRemovingManager}
                                  sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                    background: alpha(theme.palette.error.main, 0.08),
                                  }}
                                >
                                  <Trash2 size={18} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No managers assigned.
                    </Typography>
                  )}
                </Paper>

                {/* Viewers */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.2,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2.3,
                          display: "grid",
                          placeItems: "center",
                          background: alpha(theme.palette.warning.main, 0.14),
                          color: theme.palette.warning.main,
                        }}
                      >
                        <Eye size={18} />
                      </Box>
                      <Box>
                        <Typography fontWeight={900}>Viewers</Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {viewers.length} assigned
                        </Typography>
                      </Box>
                    </Stack>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setIsViewerModalOpen(true)}
                      sx={{ borderRadius: 2, fontWeight: 900 }}
                    >
                      Add
                    </Button>
                  </Stack>

                  <Divider />

                  {viewers.length ? (
                    <Stack spacing={1}>
                      {viewers.map((v) => (
                        <Paper
                          key={v._id}
                          elevation={0}
                          sx={{
                            p: 1.2,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.75) }}>
                              {v.username?.charAt(0)?.toUpperCase() || "V"}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={900} noWrap>
                                {v.username || "Unknown"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                                {v.email || "—"}
                              </Typography>
                            </Box>
                          </Stack>

                          <Tooltip title="Remove">
                            <span>
                              <IconButton
                                onClick={() => openRemove("viewer", v._id)}
                                disabled={isRemovingViewer}
                                sx={{
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                  background: alpha(theme.palette.error.main, 0.08),
                                }}
                              >
                                <Trash2 size={18} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No viewers assigned.
                    </Typography>
                  )}
                </Paper>

                {/* Sub-groups */}
                {childGroups.length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    }}
                  >
                    <Typography fontWeight={900} sx={{ mb: 1 }}>
                      📁 Sub-Groups ({childGroups.length})
                    </Typography>
                    <Stack spacing={1}>
                      {childGroups.map((c) => (
                        <Paper
                          key={c._id}
                          elevation={0}
                          onClick={() => navigate(`/groups/${c._id}`)}
                          sx={{
                            p: 1.2,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                            cursor: "pointer",
                            "&:hover": {
                              background: alpha(theme.palette.primary.main, 0.05),
                              borderColor: alpha(theme.palette.primary.main, 0.3),
                            },
                          }}
                        >
                          <Typography fontWeight={900} noWrap>
                            {c.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                            {c.groupCode || "—"}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirm.open}
        title="Remove?"
        desc="This action will unassign from this group. Continue?"
        loading={confirmLoading}
        onClose={closeRemove}
        onConfirm={handleConfirmRemove}
      />

      {/* Modals */}
      <AddViewerModal
        isOpen={isViewerModalOpen}
        onClose={() => {
          setIsViewerModalOpen(false);
          refetch();
        }}
        groupId={groupId}
      />

      <AddDeviceToGroupModal
        isOpen={isDeviceModalOpen}
        onClose={() => {
          setIsDeviceModalOpen(false);
          refetch();
          refetchStats();
        }}
        groupId={groupId}
        initialGroupDevices={Array.isArray(group?.devices) ? group.devices : []}
      />

      <AddManagerModal
        isOpen={isManagerModalOpen}
        onClose={() => {
          setIsManagerModalOpen(false);
          refetch();
        }}
        groupId={groupId}
      />
    </Box>
  );
}
